import React from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { Button } from '@heroui/react';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import type { ExpenseDraft } from '../manualImportMeta';
import type { ManualImportForm } from '../useManualImport';
import { LabeledNumberField } from './LabeledNumberField';
import { LabeledTextField } from './LabeledTextField';

type ExpenseRowProps = {
    form: ManualImportForm;
    expense: ExpenseDraft;
    suggestionsId: string;
};

/** One extra-expense row: a free-text (suggested) label and a currency amount. */
export const ExpenseRow: React.FC<ExpenseRowProps> = ({
    form,
    expense,
    suggestionsId,
}) => {
    const { t } = useTranslation('pages');

    return (
        <div
            className="grid grid-cols-1 items-start gap-3 sm:grid-cols-[1fr_1fr_auto]"
            {...getNodeTestIdProp(`manual-import-expense-${expense.key}`)}
        >
            <LabeledTextField
                label={t('purchases.manualImport.expenseLabel')}
                list={suggestionsId}
                onChange={(value) =>
                    form.onExpenseFieldChange(expense.key, 'label', value)
                }
                slot={`expense-label-${expense.key}`}
                value={expense.label}
            />
            <LabeledNumberField
                currency
                label={t('purchases.manualImport.expenseAmount')}
                onChange={(value) =>
                    form.onExpenseFieldChange(expense.key, 'amount', value)
                }
                slot={`expense-amount-${expense.key}`}
                value={expense.amount}
            />
            <div
                className="flex justify-end self-end"
                {...getNodeTestIdProp(
                    `manual-import-expense-remove-cell-${expense.key}`,
                )}
            >
                <Button
                    isIconOnly
                    onPress={() => form.onRemoveExpense(expense.key)}
                    variant="ghost"
                    {...getNodeTestIdProp(
                        `manual-import-remove-expense-${expense.key}`,
                    )}
                >
                    <Icon icon="gravity-ui:trash-bin" />
                </Button>
            </div>
        </div>
    );
};
